import _ from 'lodash';
import React from 'react';
import autobind from 'autobind-decorator';

const VIEWPAD = 10;
const Z_CONFIRM = 10500;

const mergeRefs = (...refs) => (el) => {

	_.forEach(refs, (ref) => {

		if (!ref) {

			return;
		}
		if (typeof ref === 'function') {

			ref(el);
		} else if (typeof ref === 'object') {

			ref.current = el;
		}
	});
};

@autobind
class DeleteButton extends React.Component {
	constructor (props) {
		super(props);

		this.confirmationRef = React.createRef();
		this.state = {
			showConfirmation: false,
			confirmLayout: null
		};
	}

	componentDidUpdate (prevProps, prevState) {
		const { showConfirmation } = this.state;

		if (showConfirmation && !prevState.showConfirmation) {

			window.addEventListener('resize', this.positionConfirmation, true);
			window.addEventListener('scroll', this.positionConfirmation, true);
			requestAnimationFrame(() => {

				if (this.state.showConfirmation) {

					this.positionConfirmation();
				}
			});
		}

		if (!showConfirmation && prevState.showConfirmation) {

			window.removeEventListener('resize', this.positionConfirmation, true);
			window.removeEventListener('scroll', this.positionConfirmation, true);
		}
	}

	componentWillUnmount () {

		window.removeEventListener('resize', this.positionConfirmation, true);
		window.removeEventListener('scroll', this.positionConfirmation, true);
	}

	render () {
		return (
			<div className="delete-button-container">
				{ this.renderConfirmation() }
				{ this.renderChildren() }
			</div>
		);
	}

	renderChildren () {
		const { children } = this.props;

		if (_.isEmpty(children)) {

			return this.renderDefaultButton();
		}

		return React.Children.map(children, (child) => {

			const origRef = child.ref ?? child.props?.ref;

			return React.cloneElement(child, {
				onClick: this.onDeleteClick,
				ref: mergeRefs(origRef, (el) => {

					this.triggerNode = el;
				})
			});
		});
	}

	renderDefaultButton () {
		const { label = 'Delete' } = this.props;
		return (
			<button
				ref={ (el) => {

					this.triggerNode = el;
				} }
				type="button"
				className="button alert"
				onClick={ this.onDeleteClick }>
				{ label }
			</button>
		);
	}

	renderConfirmation () {
		const { showConfirmation = false, confirmLayout } = this.state;

		if (showConfirmation !== true) {

			return null;
		}

		const {
			confirmLabel = 'Confirm',
			cancelLabel = 'Cancel',
			warningText = 'Really delete item?',
			orientation = 'center'
		} = this.props;

		const arrowStyle = confirmLayout?.arrowLeft != null ? {
			left: `${ confirmLayout.arrowLeft }px`,
			transform: 'translateX(-50%)'
		} : null;

		const panelStyle = confirmLayout ? {
			position: 'fixed',
			left: `${ confirmLayout.left }px`,
			top: `${ confirmLayout.top }px`,
			right: 'auto',
			zIndex: confirmLayout.zIndex || Z_CONFIRM,
			visibility: 'visible',
			opacity: 1
		} : {
			position: 'fixed',
			left: '-99999px',
			top: 0,
			visibility: 'hidden',
			opacity: 0,
			pointerEvents: 'none',
			zIndex: Z_CONFIRM
		};

		return (
			<div
				ref={ this.confirmationRef }
				className={ `delete-button-confirmation ${ orientation } card padding-1 delete-button-confirmation--viewport` }
				style={ panelStyle }>
				<div className="up-arrow bg" style={ arrowStyle }></div>
				<div className="up-arrow" style={ arrowStyle }></div>

				<p>{ warningText }</p>

				<div className="grid-x grid-padding-x align-middle">
					<div className="cell auto">
						<button type="button" className="button small alert margin-0 expanded" onClick={ this.onConfirmClick }>{ confirmLabel }</button>
					</div>
					<div className="cell auto">
						<button type="button" className="button small secondary margin-0 expanded" onClick={ this.onCancelClick }>{ cancelLabel }</button>
					</div>
				</div>
			</div>
		);
	}

	positionConfirmation () {

		const trigger = this.triggerNode;
		const panel = this.confirmationRef.current;

		if (!this.state.showConfirmation || !trigger || !panel) {

			return;
		}

		const tr = trigger.getBoundingClientRect();
		const w = Math.max(panel.offsetWidth, 200);
		let left = tr.left + (tr.width / 2) - (w / 2);
		left = Math.max(VIEWPAD, Math.min(left, window.innerWidth - w - VIEWPAD));
		const top = tr.bottom + VIEWPAD;
		const buttonCenter = tr.left + (tr.width / 2);
		const arrowLeft = Math.max(24, Math.min(buttonCenter - left, w - 24));

		this.setState({
			confirmLayout: {
				left,
				top,
				zIndex: Z_CONFIRM,
				arrowLeft
			}
		});
	}

	onDeleteClick (evt) {
		const { onDeleteClick = () => {} } = this.props;
		evt.stopPropagation();

		this.setState({
			showConfirmation: true,
			confirmLayout: null
		});

		onDeleteClick(evt);
	}

	onConfirmClick (evt) {
		const { onConfirmClick = () => {} } = this.props;
		evt.stopPropagation();

		this.setState({
			showConfirmation: false,
			confirmLayout: null
		});

		onConfirmClick(evt);
	}

	onCancelClick (evt) {
		const { onCancelClick = () => {} } = this.props;
		evt.stopPropagation();

		this.setState({
			showConfirmation: false,
			confirmLayout: null
		});

		onCancelClick(evt);
	}
}

export default DeleteButton;
